<template>
  <div>
    <v-card tile flat>
      <v-card-title>
        <v-icon left> mdi-calendar </v-icon>
        {{ currentDate }}
      </v-card-title>
      <v-card-text>
        <v-row align="center">
          <v-col cols="4">
            <v-select
              v-model="type"
              :items="calendarTypes"
              item-text="name"
              item-value="id"
              hide-details
              outlined
              @change="changeType"
            />
          </v-col>
          <v-col><v-spacer /></v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              large
              outlined
              :disabled="leftDisabled"
              @click="($refs.calendar || {}).prev()"
            >
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="auto">
            <v-btn
              color="primary"
              large
              outlined
              @click="($refs.calendar || {}).next()"
            >
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </v-col>
          <v-col cols="12">
            <v-calendar
              ref="calendar"
              v-model="focus"
              class="_intervalless"
              color="primary"
              event-color="white"
              :type="type"
              :weekdays="weekday"
              :interval-count="0"
              @change="updateEvents"
            >
              <template v-slot:day-label="{ date, day }">
                <v-badge
                  :color="eventIsset(date) ? 'primary' : 'transparent'"
                  offset-x="24"
                  offset-y="8"
                  bottom
                >
                  <v-btn
                    large
                    icon
                    :disabled="!eventIsset(date)"
                    :color="
                      dateIsSelected(date) || dateIsToday(date) ? 'primary' : ''
                    "
                    :outlined="dateIsSelected(date)"
                    @click="showEvents({ date })"
                  >
                    <span
                      :class="{
                        'font-weight-black primary--text': dateIsToday(date),
                        'font-weight-black': dateIsSelected(date)
                      }"
                      >{{ day }}</span
                    >
                  </v-btn>
                </v-badge>
              </template>
              <template v-slot:day-label-header="{ date, day }">
                <v-badge
                  :color="eventIsset(date) ? 'primary' : 'transparent'"
                  offset-x="24"
                  offset-y="8"
                  bottom
                >
                  <v-btn
                    large
                    icon
                    :color="
                      dateIsSelected(date) || dateIsToday(date) ? 'primary' : ''
                    "
                    :disabled="!eventIsset(date)"
                    :outlined="dateIsSelected(date)"
                    @click="showEvents({ date })"
                  >
                    <span
                      :class="{
                        'font-weight-black primary--text': dateIsToday(date),
                        'font-weight-black': dateIsSelected(date)
                      }"
                      >{{ day }}</span
                    >
                  </v-btn>
                </v-badge>
              </template>
            </v-calendar>
          </v-col>
          <v-col cols="12">
            <v-row dense>
              <v-col cols="12">
                <v-chip-group
                  v-if="dateSlots.length < 96"
                  v-model="dateSlot"
                  active-class="primary--text"
                  column
                  @change="setTime"
                >
                  <v-chip
                    v-for="slot in dateSlots"
                    :key="slot.end"
                    :value="slot"
                    label
                    outlined
                  >
                    {{ formattedSlot(slot) }}
                  </v-chip>
                  <v-col cols="12">
                    <v-input
                      v-model="dateSlot"
                      dense
                      :rules="rules.requiredSelectNotArray"
                      :errors="
                        errors.time_from || errors.time_to || errors.date_from
                      "
                    />
                  </v-col>
                </v-chip-group>

                <v-autocomplete
                  v-else
                  v-model="dateSlot"
                  :items="dateSlots"
                  outlined
                  item-value="start"
                  :item-text="formattedSlot"
                  :label="$t('ITEM.TIME')"
                  :rules="rules.requiredSelectNotArray"
                  return-object
                  @change="setTime"
                ></v-autocomplete>
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import moment from 'moment/moment';
import localable from 'vuetify/lib/mixins/localable';
import formEditable from '@/modules/_global/mixins/form-editable';
import { timeFormat, dateFormatBackend } from '@/modules/_global/filters';
import { requiredSelectNotArray } from '@/modules/_global/variables/rules';

export default {
  name: 'ReservationSlotForm',

  filters: {
    dateFormatBackend,
    timeFormat
  },

  mixins: [formEditable, localable],

  props: {
    resource: {
      type: Object,
      default: () => ({})
    },
    slots: {
      type: Array,
      required: false,
      default: () => []
    }
  },

  data() {
    return {
      lastTickedDate: null,
      type: 'week',
      weekday: [1, 2, 3, 4, 5, 6, 0],
      focus: dateFormatBackend(moment()),
      date_from: dateFormatBackend(moment()),
      date_to: dateFormatBackend(moment().endOf('month')),
      form: {
        date_from: dateFormatBackend(moment()),
        duration: 0,
        number_of_visitors: null
      },
      dateSlot: null,
      dateSlots: [],
      rules: { requiredSelectNotArray }
    };
  },

  computed: {
    currentDate() {
      return (
        moment.locale(this.currentLocale) &&
        moment(this.focus).format('MMMM YYYY')
      );
    },
    leftDisabled() {
      return moment().isAfter(this.date_from);
    },
    calendarTypes() {
      return [
        { id: 'week', name: this.$t('ITEM.WEEK') },
        { id: 'month', name: this.$t('ITEM.MONTH') }
      ];
    }
  },

  watch: {
    'form.number_of_visitors': 'getSlots',
    'form.duration': 'getSlots',
    'resource.id': 'getSlots',
    slots: 'changedSlots'
  },

  mounted() {},

  methods: {
    changedSlots() {
      this.showEvents({
        date: moment(this.form.date_from).isBetween(
          this.date_from,
          this.date_to,
          undefined,
          '[]'
        )
          ? this.form.date_from
          : null
      });

      this.setTime({ start: null, end: null });
    },
    getSlots() {
      this.$emit('getSlots', {
        params: {
          date_from: this.date_from,
          date_to: this.date_to,
          duration: this.form.duration,
          number_of_visitors: this.form.number_of_visitors
        }
      });
    },

    formattedSlot(slot) {
      return `${timeFormat(slot.start)} - ${timeFormat(slot.end)}`;
    },

    updateEvents({ start, end }) {
      this.date_from = moment().isBefore(start.date)
        ? start.date
        : dateFormatBackend(moment());
      this.date_to = moment(end.date).isAfter(start.date)
        ? end.date
        : this.date_from;

      this.getSlots();

      if (
        this.lastTickedDate &&
        moment(this.lastTickedDate).isBetween(
          start.date,
          end.date,
          undefined,
          '[]'
        )
      ) {
        this.focus = this.lastTickedDate;
        this.form.date_from = this.lastTickedDate;
      }
    },

    eventIsset(date) {
      return this.slots.find(item => dateFormatBackend(item.date) === date);
    },

    dateIsSelected(date) {
      return moment(date).isSame(this.form.date_from);
    },

    showEvents({ date }) {
      if (date) {
        this.lastTickedDate = date;
      }
      this.form.date_from = date;
      this.updateValue('date_from', this.form.date_from);
      this.dateSlots =
        (
          this.slots.find(item => {
            return dateFormatBackend(item.date) === this.form.date_from;
          }) || {}
        ).slots || [];
    },

    dateIsToday(date) {
      return moment(date).isSame(moment().format('YYYY-MM-DD'));
    },

    setTime(slot = {}) {
      this.form.time_from = timeFormat(slot.start);
      this.form.time_to = timeFormat(slot.end);
      this.updateValue('time_from', this.form.date_from);
      this.updateValue('time_to', this.form.date_from);
    },

    changeType() {
      if (this.lastTickedDate) {
        //console.log(this.lastTickedDate);
        this.focus = this.lastTickedDate;
        this.form.date_from = this.lastTickedDate;
      } else {
        this.focus = moment().format('YYYY-MM-DD');
      }
    }
  }
};
</script>

<style lang="sass">
.v-calendar._intervalless
  .v-calendar-daily__intervals-head
    display: none

  .v-calendar-daily__head
    margin: 0

  .v-calendar-weekly__day
    display: flex
    align-items: center
    justify-content: center
    height: 60px

  .v-btn--fab.v-size--default,
  .v-btn--fab.v-size--small
    height: 32px
    width: 32px

  .v-badge .v-badge__badge
    height: 4px
    min-width: 4px
    padding: 0

  .v-calendar-weekly__day_selected
    box-shadow: inset 0 0 0 2px var(--v-primary-base)
</style>
